import { createServiceFetch, createServiceFileFetch, type IServiceFile } from '~/composables/useServiceFetch';
import type { ISystemDesignServiceClient } from '~/services/interfaces/ISystemDesignServiceClient';
import type { IDesign, IDesignAccepted } from '~/types/design';
import type { IDesignRequest } from '~/types/designRequest';
import type {
    AddressLookupSource,
    IAddressLookup,
    IAddressMatchList,
    IImagerySourceList,
} from '~/types/imagerySources';
import type { ILabelSet, ILabelSetList, ILabelSetRequest, LabelExportScope } from '~/types/labels';
import type { IPublicTestHouseList } from '~/types/publicTestHouse';
import type { IRoofModel } from '~/types/roofModel';
import type { IShadowRun, IShadowRunRequest } from '~/types/shadowRun';
import type { IStaffUser } from '~/types/staff';
import { SystemDesignApiError } from '~/utils/apiErrors';
import { isServicePath, SERVICE_API } from '~/utils/servicePaths';

export class SystemDesignServiceClient implements ISystemDesignServiceClient {
    private readonly baseURL: string;
    private systemDesignFetch: ReturnType<typeof createServiceFetch>;
    private systemDesignFileFetch: ReturnType<typeof createServiceFileFetch>;

    constructor() {
        const config = useRuntimeConfig();
        this.baseURL = config.public.SYSTEM_DESIGN_DOMAIN;
        this.systemDesignFetch = createServiceFetch(this.baseURL);
        this.systemDesignFileFetch = createServiceFileFetch(this.baseURL);
    }

    async getMe(): Promise<IStaffUser> {
        return this.systemDesignFetch<IStaffUser>(`${SERVICE_API}/me`);
    }

    async createDesign(request: IDesignRequest, idempotencyKey: string): Promise<IDesignAccepted> {
        return this.systemDesignFetch<IDesignAccepted>(`${SERVICE_API}/designs`, {
            method: 'POST',
            body: request,
            headers: { 'Idempotency-Key': idempotencyKey },
        });
    }

    async getDesign(designId: string): Promise<IDesign> {
        return this.systemDesignFetch<IDesign>(`${SERVICE_API}/designs/${encodeURIComponent(designId)}`);
    }

    async getRoofModel(roofModelId: string): Promise<IRoofModel> {
        return this.systemDesignFetch<IRoofModel>(`${SERVICE_API}/roof-models/${encodeURIComponent(roofModelId)}`);
    }

    async getLabelSet(roofModelId: string): Promise<ILabelSet | null> {
        try {
            return await this.systemDesignFetch<ILabelSet>(this.labelSetPath(roofModelId));
        } catch (error) {
            if (error instanceof SystemDesignApiError && error.status === 404) return null;
            throw error;
        }
    }

    async saveLabelSet(roofModelId: string, request: ILabelSetRequest): Promise<ILabelSet> {
        return this.systemDesignFetch<ILabelSet>(this.labelSetPath(roofModelId), {
            method: 'PUT',
            body: request,
        });
    }

    async deleteLabelSet(roofModelId: string, revision: number): Promise<void> {
        await this.systemDesignFetch<unknown>(`${this.labelSetPath(roofModelId)}?revision=${revision}`, {
            method: 'DELETE',
        });
    }

    async listLabelSets(): Promise<ILabelSetList> {
        return this.systemDesignFetch<ILabelSetList>(`${SERVICE_API}/labels`);
    }

    async exportLabelSets(scope: LabelExportScope): Promise<IServiceFile> {
        return this.systemDesignFileFetch(`${SERVICE_API}/labels/export?status=${scope}`);
    }

    async listPublicTestHouses(): Promise<IPublicTestHouseList> {
        return this.systemDesignFetch<IPublicTestHouseList>(`${SERVICE_API}/public-test-houses`);
    }

    async listImagerySources(): Promise<IImagerySourceList> {
        return this.systemDesignFetch<IImagerySourceList>(`${SERVICE_API}/imagery-sources`);
    }

    /** The address travels in the body, so it stays out of URLs and their logs. */
    async geocodeAddress(address: string, imagerySource: AddressLookupSource): Promise<IAddressMatchList> {
        const lookup: IAddressLookup = { address, imagery_source: imagerySource };
        return this.systemDesignFetch<IAddressMatchList>(`${SERVICE_API}/geocode`, { method: 'POST', body: lookup });
    }

    async fetchServiceFile(path: string): Promise<Blob> {
        if (!isServicePath(path)) {
            throw new SystemDesignApiError(0, `The service returned a link the console won't load: ${path}`);
        }
        return (await this.systemDesignFileFetch(path)).blob;
    }

    async createShadowRun(request: IShadowRunRequest): Promise<IShadowRun> {
        return this.systemDesignFetch<IShadowRun>(`${SERVICE_API}/shadow-runs`, { method: 'POST', body: request });
    }

    async getShadowRun(runId: string): Promise<IShadowRun> {
        return this.systemDesignFetch<IShadowRun>(`${SERVICE_API}/shadow-runs/${encodeURIComponent(runId)}`);
    }

    async deleteShadowRun(runId: string): Promise<void> {
        await this.systemDesignFetch<unknown>(`${SERVICE_API}/shadow-runs/${encodeURIComponent(runId)}`, {
            method: 'DELETE',
        });
    }

    private labelSetPath(roofModelId: string): string {
        return `${SERVICE_API}/roof-models/${encodeURIComponent(roofModelId)}/labels`;
    }
}
